import React, { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import { DocumentsPreviewDocument } from 'generated/resources/graphql'
import { useInterval } from 'usehooks-ts'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import {
  isBouncedSignatures,
  isCancelledSignatures,
  isDeclinedSignatures,
  isErrorSignatures,
  isExpiredSignatures,
  isInTrashBin,
  isPartiallySignedSignatures,
  isPendingSignatures,
  isPendingToFill,
  isPublicCompanyDocument,
} from 'modules/documents/lib/Documents'
import DocumentLogs from 'modules/documents/store/collections/logs'
import type DocumentLog from 'modules/documents/store/models/Log'
import { useGetEsignatureRequests } from 'modules/esignatures/hooks/requests/useGetEsignatureRequests'
import { useBulkResendRequestSignature } from 'modules/esignatures/hooks/signatures/useBulkResendRequestSignature'
import { useGetEsignatureSignatures } from 'modules/esignatures/hooks/signatures/useGetEsignatureSignatures'

import AuditTrailModal from './AuditTrailModal'
import MessageAlert from './MessageAlert'
import EsignatureModal from './Modal'
import SignaturesList from './SignaturesList'
import EsignatureTitle from './Title'

interface Props {
  document: Document
}

const ESIGNATURES_REFRESH_INTERVAL = 5000

const Esignature = ({ document }: Props) => {
  const [open, setOpen] = useState(false)
  const [openConfirmAuditTrail, setOpenConfirmAuditTrail] = useState(false)
  const [documentLogs] = useState(new DocumentLogs())
  const { data: data } = useQuery(DocumentsPreviewDocument, {
    variables: { folderId: document.folder?.id || -1 },
  })
  const currentFolder = data?.documents.folders[0]
  const canManageFolder = !!currentFolder?.managePermission
  const canUseCompanyDocuments = data?.documents._policies?.companyUseCompanyDocuments.can
  const canManageEsignature = canManageFolder || canUseCompanyDocuments

  const fetchLogs = (documentId: number) =>
    documentLogs.fetch({ data: { document_id: documentId, log_type: 'file_updated' } })

  const { loadingQL, signatures, startSignaturesPolling, stopSignaturesPolling } =
    useGetEsignatureSignatures(document.id)
  const { signatureRequests: signatureRequests } = useGetEsignatureRequests(document.id)

  const { bulkResendRequestSignature } = useBulkResendRequestSignature()

  const { loading } = useAsyncData(
    () => Promise.all([fetchLogs(document.id)]),
    [document.id, document.folder?.id]
  )

  const pendingSignatures = signatures.filter((signature) => signature.pending)

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const currentAccessRemoteUrl = signatures.find(
    (signature) => signature.pending && signature.signee.id === accessId
  )?.remoteUrl

  const hasPendingSignatures = !loadingQL && pendingSignatures.length > 0

  const showAuditTrailDownload =
    canManageEsignature &&
    (!hasPendingSignatures || isDeclinedSignatures(document)) &&
    signatures.length > 0 &&
    !isCancelledSignatures(document) &&
    !isBouncedSignatures(document)

  const description = () => {
    if (hasPendingSignatures) {
      return i18n.t('documents.modal.esignature.description_pending')
    }

    if (signatures.length > 0) {
      return i18n.t('documents.modal.esignature.description_signed')
    }

    return i18n.t('documents.modal.esignature.description')
  }

  const indexedLogs = documentLogs.models.reduce(
    (indexed: { [id: number]: boolean }, log: DocumentLog) => {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const id: number = parseInt(log.attributes.payload['esignature_id'] as string)
      indexed[id] = true
      return indexed
    },
    {}
  )

  const signedSignatures = signatures.filter((signature) => signature.signed)

  const completeUpdated = signedSignatures.every((signature) => indexedLogs[signature.id])

  const noSignatures = !signatures.length

  const waitingForSignatures = isPendingSignatures(document) && noSignatures

  const signatureRequestable =
    !isPendingSignatures(document) && noSignatures && !document.signatureStatus

  const showProcessingSignatureAlert = !completeUpdated

  const hideEsignature = isPublicCompanyDocument(document) && !hasPendingSignatures && noSignatures

  const showResendSignature =
    (isPendingSignatures(document) || isPartiallySignedSignatures(document)) &&
    !isPendingToFill(document)

  const showSignatureList = signatures.length > 0 || isErrorSignatures(document)
  const requestDate = signatureRequests[0]?.createdAt

  const resendSingnatureReminder = async () => {
    const result = await bulkResendRequestSignature({ documentIds: [document.id] })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.done'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }
  }

  useInterval(() => {
    if (waitingForSignatures) startSignaturesPolling(ESIGNATURES_REFRESH_INTERVAL)
    else stopSignaturesPolling()

    if (!completeUpdated) return fetchLogs(document.id)
  }, ESIGNATURES_REFRESH_INTERVAL)

  const showTitle = signatures.length > 0 || !isInTrashBin(document)

  if (loading || loadingQL) {
    return (
      <Box alignItems="center">
        <Spinner />
      </Box>
    )
  }

  if (hideEsignature) return null

  const auditTrailDownloadButton = () => (
    <Button onClick={() => setOpenConfirmAuditTrail(true)} iconName="download">
      {i18n.t('documents.modal.esignature.download_audit_trail')}
    </Button>
  )

  return (
    <Box gap="s16">
      {open && <EsignatureModal document={document} onClose={() => setOpen(false)} />}
      {showTitle && <EsignatureTitle description={description()} />}
      {showSignatureList && document.signatureStatus && (
        <SignaturesList
          signatures={signatures}
          requestDate={requestDate}
          documentId={document.id}
          documentSignatureStatus={document.signatureStatus}
        />
      )}
      {isCancelledSignatures(document) && pendingSignatures.length > 0 && (
        <MessageAlert
          message={i18n.t('documents.modal.esignature.cancelling_alert')}
          alertColor="info"
          visibleLoader={false}
        />
      )}
      {isCancelledSignatures(document) && pendingSignatures.length === 0 && (
        <MessageAlert
          message={i18n.t('documents.modal.esignature.cancelled_alert')}
          helpUrl="esignature_request"
          helpText={i18n.t('documents.modal.esignature.learn_more')}
          alertColor="error"
          visibleLoader={false}
          id="canceledSignatureMessage"
        />
      )}

      {!isInTrashBin(document) && (
        <>
          {isPendingSignatures(document) && noSignatures && (
            <MessageAlert message={i18n.t('documents.modal.esignature.pending_alert')} />
          )}
          {showProcessingSignatureAlert && (
            <MessageAlert
              message={i18n.t('documents.modal.esignature.processing_signature_alert')}
            />
          )}
          {isBouncedSignatures(document) && (
            <MessageAlert
              message={i18n.t('documents.modal.esignature.bounced_email_alert')}
              helpUrl="esignature_request"
              helpText={i18n.t('documents.modal.esignature.learn_more')}
              alertColor="error"
              visibleLoader={false}
            />
          )}
          {isDeclinedSignatures(document) && (
            <MessageAlert
              message={i18n.t('documents.modal.esignature.declined_alert')}
              alertColor="warning"
              visibleLoader={false}
            />
          )}
          {isErrorSignatures(document) && (
            <MessageAlert
              message={i18n.t('documents.modal.esignature.signature_error_alert')}
              alertColor="error"
              visibleLoader={false}
            />
          )}

          <Box alignItems="flexEnd" gap="s8">
            {currentAccessRemoteUrl &&
              !isErrorSignatures(document) &&
              !isExpiredSignatures(document) && (
                <Button
                  onClick={() => window.location.assign(currentAccessRemoteUrl)}
                  iconName="sign"
                >
                  {i18n.t('documents.modal.esignature.sign_button')}
                </Button>
              )}
            {signatureRequestable && (
              <Button onClick={() => setOpen(true)}>
                {i18n.t('documents.modal.esignature.request_esignature')}
              </Button>
            )}
            {showAuditTrailDownload && auditTrailDownloadButton()}
            {showResendSignature && (
              <Button type="secondary" onClick={() => resendSingnatureReminder()} iconName="bell">
                {i18n.t('documents.modal.esignature.resend_signature')}
              </Button>
            )}
          </Box>
        </>
      )}

      {isInTrashBin(document) && showAuditTrailDownload && (
        <Box alignItems="flexEnd" gap="s8">
          {auditTrailDownloadButton()}
        </Box>
      )}

      {openConfirmAuditTrail && signatures[0] && (
        <AuditTrailModal
          signature={signatures[0]}
          onClose={() => setOpenConfirmAuditTrail(false)}
        />
      )}
    </Box>
  )
}

export default observer(Esignature)
